import { Price } from '@vfde-brix/ws10/core';
import {
    JarvisPrice,
    JarvisProduct,
    JarvisTvProduct,
} from '../../interface';
import {
    getCMSConfig,
    isYoungPromoActive,
} from '../cms';
import { getDiscountedPrice } from '../formatter';
import {
    CmsPlaceholder,
    JarvisPriceType,
} from '../../enum';
import {
    IPromoPriceProperties,
    PromoPriceVariant,
} from '@vfde-brix/ws10/promo-price';
import { IOfferSummaryCardPromoPrice } from '@vfde-brix/ws10/offer-summary-card';
import { DeepPartial } from '@vfde-brix/ws10/core/types/Utils';
import { isNotNullOrUndefined } from '../typeGuard';

const getPriceValues = (price: number, tvPrice: number, discountPrice?: number): Partial<IPromoPriceProperties> =>
    discountPrice ? {
        price: Price.fromNumber(getDiscountedPrice(price + tvPrice, discountPrice)),
        strikePrice: Price.fromNumber(getDiscountedPrice(price + tvPrice)),
    } : {
        price: Price.fromNumber(getDiscountedPrice(price + tvPrice)),
        strikePrice: undefined,
    };

/**
 * Gets properties to be updated on for the price element on the OfferSummaryCard component
 *
 * @param price - The selected tariff's price
 * @param tvPrice - Price of the tv product
 * @param discount - Discount applied to price
 */
export const getTariffPriceProps = (
    price: JarvisPrice,
    tvPrice: number,
    discount: number,
): DeepPartial<IOfferSummaryCardPromoPrice> | null => {
    const { textAfterPrice } = getCMSConfig().offerSummaryCard;

    switch (price.type) {
        case JarvisPriceType.ONCE:
            return {
                containerPromoPrice: getPriceValues(price.value, tvPrice, discount),
                txtLabel: textAfterPrice.once,
            };
        case JarvisPriceType.MONTHLY:
            return {
                containerPromoPrice: getPriceValues(price.value, tvPrice, discount),
                txtLabel: textAfterPrice.monthly,
            };
        case JarvisPriceType.STAIRWAY:
            if (!price.stairway) {
                // Jarvis error
                return null;
            }

            return {
                containerPromoPrice: getPriceValues(price.stairway.value, tvPrice, discount),
                txtLabel: textAfterPrice.monthly,
            };
    }
};

/**
 * Gets properties for stairway promo price
 *
 * @param price - The selected tariff's price
 * @param tvPrice - Price of the tv product
 * @param discount - Discount applied to price
 */
export const getStairwayPromoPrice = (
    price: JarvisPrice,
    tvPrice: number,
    discount: number,
): DeepPartial<IOfferSummaryCardPromoPrice> | null=> {
    if (!discount || !price.stairway) {
        return null;
    }

    const cmsStairwayText = getCMSConfig().offerSummaryCard.legalText.stairway;

    return {
        containerPromoPrice: {
            optSizeVariation: PromoPriceVariant.Small,
            price: Price.fromNumber(getDiscountedPrice(price.value + tvPrice, discount)),
            strikePrice: Price.fromNumber(price.value + tvPrice),
        },
        txtLabel: cmsStairwayText.replace(CmsPlaceholder.STAIRWAY_MONTH, (price.stairway.period + 1).toString()),
    };
};

/**
 * Get promo prices for OfferSummaryCard component
 *
 * @param tariff - The selected tariff
 * @param tvProduct - The availabe tv product
 * @param isYoung - If customer chose young
 * @param isFriendsAndFamily - If customer has F&F
 * @param isTVProductSelected - If customer chose the tv product
 * @param isContentEnabled - If tariffs are available or not
 */
export const getPromoPrices = (
    tariff: JarvisProduct | undefined,
    tvProduct: JarvisTvProduct | undefined,
    isYoung: boolean,
    isFriendsAndFamily: boolean,
    isTVProductSelected: boolean,
    isContentEnabled: boolean,
): DeepPartial<IOfferSummaryCardPromoPrice>[] => {
    if (!isContentEnabled || !tariff) {
        return [];
    }

    const { price, friendsAndFamilyDiscount, youngPromoDiscount, minorityNE4Discount } = tariff;

    const isYoungPromo = isYoung && isYoungPromoActive();

    const tvPrice = isTVProductSelected && tvProduct ? tvProduct.discountedPrice.value : 0;
    const friendsAndFamilyDiscountValue = isFriendsAndFamily && friendsAndFamilyDiscount ? friendsAndFamilyDiscount.price.value : 0;
    const minorityNE4DiscountValue = minorityNE4Discount ? minorityNE4Discount.price.value : 0;
    const youngPromoDiscountValue = isYoungPromo && youngPromoDiscount ? youngPromoDiscount.price.value : 0;

    const discount = friendsAndFamilyDiscountValue + minorityNE4DiscountValue;

    return [
        getTariffPriceProps(price, tvPrice, discount),
        getStairwayPromoPrice(price, tvPrice, discount + youngPromoDiscountValue),
    ].filter(isNotNullOrUndefined);
};
